import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

interface ProgressiveImageProps {
  src: string;
  thumbnailSrc?: string;
  alt: string;
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
}

export const ProgressiveImage = ({
  src,
  thumbnailSrc,
  alt,
  className,
  onLoad,
  onError,
}: ProgressiveImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isThumbLoaded, setIsThumbLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Preload the full image
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.referrerPolicy = "no-referrer";
    img.onload = () => {
      setIsLoaded(true);
      onLoad?.();
    };
    img.onerror = () => {
      setHasError(true);
      onError?.();
    };
    img.src = src;
  }, [src, onLoad, onError]);

  if (hasError) {
    return (
      <div className={cn("bg-gray-800 flex items-center justify-center", className)}>
        <div className="text-center text-gray-400">
          <div className="text-4xl mb-2">🖼️</div>
          <p className="text-sm">Image not available</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("relative overflow-hidden", className)}>
      {/* Thumbnail */}
      {thumbnailSrc && !isLoaded && (
        <img
          src={thumbnailSrc}
          alt={alt}
          className={cn(
            "absolute inset-0 w-full h-full object-cover transition-opacity duration-300",
            isThumbLoaded ? "opacity-100" : "opacity-0",
          )}
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
          onLoad={() => setIsThumbLoaded(true)}
        />
      )}

      {/* Loading placeholder */}
      {!isThumbLoaded && !isLoaded && (
        <div className="absolute inset-0 bg-gray-800 animate-pulse flex items-center justify-center">
          <div className="text-white/60">Loading...</div>
        </div>
      )}

      {/* Full resolution image */}
      <img
        src={src}
        alt={alt}
        className={cn(
          "w-full h-full object-cover transition-opacity duration-300",
          isLoaded ? "opacity-100" : "opacity-0",
        )}
        crossOrigin="anonymous"
        referrerPolicy="no-referrer"
      />
    </div>
  );
};
